import type { PageProps } from 'gatsby';
import { graphql } from 'gatsby';
import { DateTime } from 'luxon';
import React from 'react';

import { ArticleContent } from '../../components/article-content';
import { FeatureTitle } from '../../components/feature-title';
import { LoginWrapper } from '../../components/login-wrapper';
import { NewsletterHero } from '../../components/newsletter-hero';
import { NextArticle } from '../../components/next-article';
import { ProtectedContent } from '../../components/protected-content';
import { DefaultHead, DefaultLayout } from '../../layouts/default-layout';

const buildArticleMetadata = (article: Queries.ArticlePageQuery['article']): string => {
	if (!article) return '';
	return [
		// Use fixed format eg. 1-Aug-2023
		...(article.date ? [DateTime.fromISO(article.date).toFormat('d-MMM-y')] : []),
		...(article.location ? [article.location] : []),
	].join(' · ');
};

const ArticlePage = ({ data }: PageProps<Queries.ArticlePageQuery>): JSX.Element => {
	if (!data.article) return <p>Could not render article</p>;

	const {
		isProtected,
		groupsAllowedToView,
		contentful_id,
		content,
		title,
		collection,
		summary,
		nextArticle,
		mailchimpInterestId,
	} = data.article;
	// Protected article will load client-side
	if (isProtected) {
		return (
			<DefaultLayout>
				<LoginWrapper allowedGroups={groupsAllowedToView as any}>
					<ProtectedContent articleId={contentful_id} />
				</LoginWrapper>
			</DefaultLayout>
		);
	}

	return (
		<DefaultLayout
			breadcrumb={{
				category: collection?.categoryPlural,
				title: title,
				slug: collection?.slug,
			}}
			mailchimpInterestId={mailchimpInterestId ?? undefined}
		>
			<ArticleContent
				renderTitle={() => {
					return <FeatureTitle title2={title}>{buildArticleMetadata(data.article)}</FeatureTitle>;
				}}
				renderNextArticle={() =>
					!!nextArticle &&
					!!nextArticle.title &&
					!!nextArticle.slug && (
						<NextArticle
							title1={`Next ${collection?.category ?? ''}`}
							title2={nextArticle?.title ?? undefined}
							slug={nextArticle?.slug ?? undefined}
						/>
					)
				}
				content={content ?? undefined}
				summary={summary?.childMarkdownRemark?.html ?? undefined}
			/>
		</DefaultLayout>
	);
};

// @todo add some kind of parent linking so we can filter better
export const query = graphql`
	query ArticlePage($id: String) {
		article: contentfulArticle(id: { eq: $id }) {
			...ArticleFragment
		}
	}
`;

const buildArticleTitle = (article?: Queries.ArticlePageQuery['article']): string => {
	return [article?.collection?.categoryPlural, article?.title].filter((i) => !!i).join(' - ');
};

export const Head = ({ data }: PageProps<Queries.ArticlePageQuery>) => (
	<DefaultHead title={buildArticleTitle(data.article ?? undefined)} />
);

export default ArticlePage;
